import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signOut } from 'firebase/auth';
import { doc, writeBatch } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { Building2, ArrowRight, Users, Minus, Plus, LogOut } from 'lucide-react';
import { nanoid } from 'nanoid';
import { PRICING } from '../../utils/subscriptionUtils';

export default function CompanySetup({ user, profile, onComplete }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: '',
    industry: '',
    teamSize: '',
    userCount: 1,
    country: 'GB',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.companyName.trim()) {
      setError(t('setup.companyNameRequired', 'Company name is required.'));
      return;
    }
    setLoading(true);
    setError('');

    try {
      const companyId = nanoid(12);
      const batch = writeBatch(db);

      batch.set(doc(db, 'companies', companyId), {
        name: formData.companyName.trim(),
        companyName: formData.companyName.trim(),
        industry: formData.industry,
        teamSize: formData.teamSize,
        shiftsUserLimit: Math.max(1, Math.min(1000, parseInt(formData.userCount, 10) || 1)),
        country: formData.country,
        createdBy: user.uid,
        createdAt: new Date().toISOString(),
        shiftsSubscriptionStatus: 'trialing',
        shiftsTrialEndsAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
        shiftsSettings: {
          timezone: 'Europe/London',
          weekStartDay: 1,
          defaultShiftDuration: 8,
          minRestHours: 11,
          maxWeeklyHours: 48,
          maxDailyHours: 13,
          maxConsecutiveDays: 6,
          enableAISuggestions: true,
          enableAutoAssign: false,
          requireSwapApproval: true,
          notifyOnPublish: true,
          notifyOnSwapRequest: true,
          notifyOnShiftChange: true,
          notifyBeforeShift: 60,
          showCoverageWarnings: true,
        },
      });

      batch.set(doc(db, 'companies', companyId, 'users', user.uid), {
        email: user.email,
        displayName: profile?.displayName || user.displayName || '',
        role: 'admin',
        active: true,
        joinedAt: new Date().toISOString(),
      });

      batch.set(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: profile?.displayName || user.displayName || '',
        active: true,
        product: 'buildmyshifts',
        shiftsCompanyId: companyId,
        shiftsRole: 'admin',
      }, { merge: true });

      await batch.commit();

      navigate('/trial-success', { replace: true });
      if (onComplete) {
        setTimeout(() => onComplete(companyId), 0);
      }
    } catch (err) {
      console.error('Company setup error:', err);
      setError(err.message || t('setup.createCompanyFailed', 'Failed to create company.'));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center px-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-2">
            <img src="/logo.png" alt="BuildMyShifts" className="w-12 h-12 object-contain" />
            <h1 className="text-3xl font-bold text-gray-900">
              Build My <span className="text-primary">Shifts</span>
            </h1>
          </div>
          <p className="text-gray-500 mt-2">{t('setup.subtitle', 'Set up your company to get started')}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <Building2 size={24} className="text-primary" />
            <h2 className="text-xl font-bold text-gray-900">{t('setup.title', 'Company Setup')}</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('setup.companyName', 'Company Name')} *</label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary/50 focus:border-primary"
                placeholder={t('setup.companyPlaceholder', 'Acme Ltd')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('setup.industry', 'Industry')}</label>
              <select
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5"
              >
                <option value="">{t('setup.selectIndustry', 'Select industry')}</option>
                <option value="hospitality">{t('setup.industries.hospitality', 'Hospitality')}</option>
                <option value="retail">{t('setup.industries.retail', 'Retail')}</option>
                <option value="healthcare">{t('setup.industries.healthcare', 'Healthcare')}</option>
                <option value="construction">{t('setup.industries.construction', 'Construction')}</option>
                <option value="manufacturing">{t('setup.industries.manufacturing', 'Manufacturing')}</option>
                <option value="logistics">{t('setup.industries.logistics', 'Logistics')}</option>
                <option value="security">{t('setup.industries.security', 'Security')}</option>
                <option value="education">{t('setup.industries.education', 'Education')}</option>
                <option value="technology">{t('setup.industries.technology', 'Technology')}</option>
                <option value="other">{t('setup.industries.other', 'Other')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('setup.teamSize', 'Team Size')}</label>
              <select
                value={formData.teamSize}
                onChange={(e) => setFormData({ ...formData, teamSize: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5"
              >
                <option value="">{t('setup.selectTeamSize', 'Select team size')}</option>
                <option value="1-5">{t('setup.teamSizes.1_5', '1-5 employees')}</option>
                <option value="6-15">{t('setup.teamSizes.6_15', '6-15 employees')}</option>
                <option value="16-50">{t('setup.teamSizes.16_50', '16-50 employees')}</option>
                <option value="51-100">{t('setup.teamSizes.51_100', '51-100 employees')}</option>
                <option value="100+">{t('setup.teamSizes.100_plus', '100+ employees')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('setup.userCount', 'Number of Users')} *</label>
              <p className="text-xs text-gray-400 mb-2">
                {t('setup.userCountHelp', 'How many staff will use BuildMyShifts? (£{{price}}/user/month)', {
                  price: PRICING.STANDALONE.toFixed(2),
                })}
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, userCount: Math.max(1, formData.userCount - 1) })}
                  disabled={formData.userCount <= 1}
                  className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Minus size={16} />
                </button>
                <input
                  type="number"
                  value={formData.userCount}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value) && value >= 1 && value <= 1000) {
                      setFormData({ ...formData, userCount: value });
                    }
                  }}
                  className="w-20 text-center text-xl font-bold text-gray-900 border border-gray-300 rounded-lg py-2 focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  min={1}
                  max={1000}
                />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, userCount: Math.min(1000, formData.userCount + 1) })}
                  disabled={formData.userCount >= 1000}
                  className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Plus size={16} />
                </button>
                <Users size={18} className="text-gray-400 ml-1" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('setup.country', 'Country')}</label>
              <select
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5"
              >
                <option value="GB">{t('setup.countries.GB', 'United Kingdom')}</option>
                <option value="US">{t('setup.countries.US', 'United States')}</option>
                <option value="DE">{t('setup.countries.DE', 'Germany')}</option>
                <option value="FR">{t('setup.countries.FR', 'France')}</option>
                <option value="ES">{t('setup.countries.ES', 'Spain')}</option>
                <option value="PT">{t('setup.countries.PT', 'Portugal')}</option>
                <option value="PL">{t('setup.countries.PL', 'Poland')}</option>
                <option value="RO">{t('setup.countries.RO', 'Romania')}</option>
                <option value="IN">{t('setup.countries.IN', 'India')}</option>
                <option value="CN">{t('setup.countries.CN', 'China')}</option>
                <option value="AU">{t('setup.countries.AU', 'Australia')}</option>
              </select>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-white rounded-lg py-3 font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? t('setup.creating', 'Creating...') : t('setup.createCompany', 'Create Company & Start Trial')}
              <ArrowRight size={18} />
            </button>
          </form>

          <p className="mt-4 text-center text-xs text-gray-400">
            {t('setup.trialNote', '14-day free trial. No credit card required.')}
          </p>

          <button
            type="button"
            onClick={handleLogout}
            className="mt-4 w-full text-center text-sm text-gray-400 hover:text-red-400 transition-colors flex items-center justify-center gap-2"
          >
            <LogOut size={14} />
            {t('nav.logout', 'Log Out')}
          </button>
        </div>
      </div>
    </div>
  );
}
